import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import ApiService from '@/core/services/api.service';
import _ from 'lodash';

const DebtRepository = RepositoryFactory.get('debts');

/**
 * DebtProvidersService - Business logic layer for debt providers functionality
 * Handles data transformation and business rules for the debt providers list component
 */
export default {
  /**
   * Get provider debts with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Formatted debt data
   */
  async getProviderDebts(params) {
    try {
      const response = await DebtRepository.getProviderDebts(params);
      return response;
    } catch (error) {
      console.error('Service error fetching provider debts:', error);
      throw error;
    }
  },

  /**
   * Get all providers formatted for autocomplete component
   * @returns {Promise<Array>} Formatted provider list for autocomplete
   */
  async searchProvidersForAutocomplete(searchText) {
    try {
      const res = await ApiService.query('v1/customers', {
        params: { searchText, size: 10 },
      });
      return (res.data?.data?.items || []).map((c) => ({
        id: c.id,
        name: c.fullName,
        suggestionName: c.fullName,
      }));
    } catch (error) {
      console.error('Service error searching providers:', error);
      return [];
    }
  },

  /**
   * Calculate sum of amounts for a specific field
   * @param {Array} dataset - Array of debt records
   * @param {string} key - Field name to sum
   * @returns {number} Sum of the specified field
   */
  calculateSumAmount(dataset, key) {
    return _.reduce(
      dataset,
      (sum, item) => {
        return sum + parseFloat(item[key] || 0);
      },
      0
    );
  },

  /**
   * Calculate sum of calculated amounts (difference between two fields)
   * @param {Array} dataset - Array of debt records
   * @param {string} key1 - First field name
   * @param {string} key2 - Second field name
   * @returns {number} Sum of positive differences
   */
  calculateSumCalAmount(dataset, key1, key2) {
    return _.reduce(
      dataset,
      (sum, item) => {
        const amount1 = parseFloat(item[key1] || 0);
        const amount2 = parseFloat(item[key2] || 0);
        const calVal = amount1 - amount2;
        return sum + (calVal > 0 ? calVal : 0);
      },
      0
    );
  },

  /**
   * Filter providers based on search text
   * @param {Array} providers - Provider list
   * @param {string} searchText - Search query
   * @param {number} limit - Maximum number of results
   * @returns {Array} Filtered provider list
   */
  /**
   * Build query object for debt detail navigation
   * @param {Object} item - Debt provider item
   * @param {string} fromDate - Start date filter
   * @param {string} toDate - End date filter
   * @returns {Object} Query object for router navigation
   */
  buildDetailQuery(item, fromDate, toDate) {
    return {
      providerId: item.id,
      providerName: item.name,
      fromDate: fromDate,
      toDate: toDate,
    };
  },

  /**
   * Build transaction parameters for navigation
   * @param {Object} item - Debt provider item
   * @param {string} contactType - Contact type (e.g., '2' for provider)
   * @param {string} type - Transaction type
   * @returns {Object} Transaction parameters for router navigation
   */
  buildTransactionParams(item, contactType, type) {
    return {
      mainModelProp: {
        contactType: contactType,
        type: type,
        contactId: item.id,
        contactName: item.name,
      },
    };
  },

  /**
   * Validate filter parameters
   * @param {Object} filters - Filter object
   * @returns {Object} Validated and cleaned filter parameters
   */
  validateFilters(filters) {
    const cleanFilters = { ...filters };
    
    // Clean up provider selection if search is empty
    if (!cleanFilters.searchProvider || cleanFilters.searchProvider.trim() === '') {
      cleanFilters.providerId = null;
    }
    
    return cleanFilters;
  },
};
